import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MgsFieldDefaultComponent } from './mgs-field-default.component';
import { INumberFieldOptions } from '@mng/model';
import {
  onlyNumberKeysAllowed,
  onlyNumberKeysAndCommasAllowed,
  onlyNumberKeysAndMinusAllowed,
  onlyNumberKeysCommasAndMinusAllowed
} from '@mng/mng-keyboard-helper';
import { ATTRIBUT_TYPES } from '@mng/mgs-lib';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MgsNormHandlerComponent } from './mgs-norm-handler.component';
import { NgStyle } from '@angular/common';
import { validatorRegex } from '@mng/mng-inputs';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatError, MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

export const numberPatternWithDecimals = /^[0-9]*(,\d{1,2})?$/;
export const numberPattern = /^[0-9]*$/;

@Component({
  selector: 'mng-mgs-field-numeric-component',
  imports: [
    ReactiveFormsModule,
    MgsNormHandlerComponent,
    NgStyle,
    MatInput,
    MatFormField,
    MatSuffix,
    MatError,
    MatIcon,
    MatIconButton,
    MatTooltip
  ],
  template: `
    <div style="width: 100%; display: flex; gap: 20px; align-items: center;" [ngStyle]="style">
      <mat-form-field
        style="padding-bottom: 0; color: black; width: 280px;"
        [appearance]="appearance"
        color="accent"
        [attr.id]="defId"
        subscriptSizing="dynamic"
      >
        <input
          matInput
          type="text"
          inputmode="numeric"
          [formControl]="formControl"
          autocomplete="off"
          (keydown)="preventNotAllowedKeys($event)"
        />
        @if (uiAttribut.attribut.definition.einheit) {
          <span matTextSuffix style="font-size: x-small;">
            {{ uiAttribut.attribut.definition.einheit.label }}
          </span>
        }

        @if (formControl.errors) {
          <mat-error>{{ getErrorMessage() }}</mat-error>
        }
      </mat-form-field>

      <div style="width: 100px; font-weight: bold; font-size: 10px;">
        @if (uiAttribut.vorUntersuchungWert) {
          [VU: {{ uiAttribut.vorUntersuchungWert }}]
        }
      </div>

      <div style="width: 300px;">
        @if (uiAttribut.attribut.normDefinition || uiAttribut.attribut.normWert) {
          <mng-mgs-norm-handler-component [uiAttribut]="uiAttribut" />
        }
      </div>

      <div style="width: 50px">
        @if (uiAttribut.vorUntersuchungWert && dienstleistung().bearbeitbar) {
          <button
            mat-icon-button
            (click)="untersuchungsVergleichPdf()"
            color="accent"
            matTooltip="Untersuchungsvergleich"
            tabindex="-1"
          >
            <mat-icon svgIcon="chart-bar-stacked" />
          </button>
        }
      </div>

      @if (uiAttribut.punkteAnzeigeInfo && uiAttribut.punkteAnzeigeInfo().punkteInfoRelevant) {
        @if (uiAttribut.punkteAnzeigeInfo().minPunkteAnzeige) {
          <div class="flex">
            <mat-icon svgIcon="alert-outline" color="warning" />
            <div>
              Die Anzahl der gewählten Punkte muss mindestens
              <strong>{{ uiAttribut.punkteAnzeigeInfo().minPunkte }}</strong> betragen (max.
              {{ uiAttribut.punkteAnzeigeInfo().maxPunkte }})
            </div>
          </div>
        } @else if (uiAttribut.punkteAnzeigeInfo().maxPunkte) {
          <div class="flex">
            <mat-icon svgIcon="info_outline" color="info" />
            <div>Maximale Punkteanzahl: {{ uiAttribut.punkteAnzeigeInfo().maxPunkte }}</div>
          </div>
        }
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MgsFieldNumericComponent extends MgsFieldDefaultComponent {
  pattern: RegExp | undefined;

  numberFieldOptions: INumberFieldOptions;

  constructor() {
    super();
  }

  attributSet() {
    super.attributSet();

    const validators = this.attribut.mandatory ? [Validators.required] : [];

    if (this.definition?.validation?.regexPattern) {
      const realRegexPattern = `^${this.definition.validation.regexPattern}$`;
      try {
        this.pattern = new RegExp(realRegexPattern);
      } catch (error) {
        console.error(
          'Fehler beim Setzen der Regex - Definition Regex: ',
          this.definition.validation.regexPattern,
          'Angepasst',
          realRegexPattern,
          error
        );
      }
    }

    if (!this.pattern) {
      if (ATTRIBUT_TYPES.TYPE_DECIMAL === this.definition.attributTypeId) {
        this.pattern = numberPatternWithDecimals;
      } else if (ATTRIBUT_TYPES.TYPE_NUMERIC === this.definition.attributTypeId) {
        this.pattern = numberPattern;
      }
    }
    validators.push(validatorRegex(this.pattern));

    this.formControl.setValidators(validators);

    if (ATTRIBUT_TYPES.TYPE_DECIMAL === this.definition.attributTypeId) {
      this.numberFieldOptions = { allowDecimal: true };
    }
  }

  hasPatternError(): boolean {
    return this.formControl.errors?.['pattern'];
  }

  getErrorMessage(): string {
    if (this.definition?.validation?.message) {
      return this.definition?.validation?.message;
    } else {
      return this.validationMessage;
    }
  }

  preventNotAllowedKeys(event: KeyboardEvent) {
    const options = this.numberFieldOptions;

    if (options) {
      if (options.allowNegative && options.allowDecimal) {
        if (!onlyNumberKeysCommasAndMinusAllowed(event)) {
          event.preventDefault();
        }
      } else if (options.allowDecimal) {
        if (!onlyNumberKeysAndCommasAllowed(event)) {
          event.preventDefault();
        }
      } else if (options.allowNegative) {
        if (!onlyNumberKeysAndMinusAllowed(event)) {
          event.preventDefault();
        }
      } else {
        if (!onlyNumberKeysAllowed(event)) {
          event.preventDefault();
        }
      }
    } else {
      if (!onlyNumberKeysAllowed(event)) {
        event.preventDefault();
      }
    }
  }

  untersuchungsVergleichPdf() {
    void this.service.untersuchungsVergleichPdfAnzeige(this.defId);
  }
}
